import React from "react";
import {
  Star, MapPin, MessageSquare, CheckCircle2, Camera, Clock,
  Phone, Globe, Building2, Zap, TrendingUp, Users,
} from "lucide-react";

const getScoreColor = (s) => {
  if (s >= 80) return "text-emerald-400";
  if (s >= 60) return "text-yellow-400";
  if (s >= 40) return "text-orange-400";
  return "text-red-400";
};

const getScoreLabel = (s) => {
  if (s >= 80) return "Excellent";
  if (s >= 60) return "Good";
  if (s >= 40) return "Fair";
  return "Needs Work";
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case "critical": return "bg-red-500/20 border-red-500/30 text-red-400";
    case "high": return "bg-orange-500/20 border-orange-500/30 text-orange-400";
    case "medium": return "bg-yellow-500/20 border-yellow-500/30 text-yellow-400";
    default: return "bg-slate-700/50 border-slate-600/30 text-slate-300";
  }
};

export const ReportGBP = ({ reviewsData, brandInput }) => {
  const gbpRecs = reviewsData?.gbp_recommendations || {};
  const companyData = reviewsData?.company || {};
  const gbpData = companyData?.gbp_data || {};
  const score = gbpRecs.overall_score || 0;
  const scoreBreakdown = gbpRecs.score_breakdown || {};

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold text-white text-lg mb-1">Google Business Profile Score</h3>
            <p className="text-slate-400 text-sm">Based on profile completeness, reviews, and optimization</p>
          </div>
          <div className="text-center">
            <p className={`text-5xl font-bold ${getScoreColor(score)}`}>{score}</p>
            <p className={`text-sm ${getScoreColor(score)}`}>{getScoreLabel(score)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-slate-400">Rating</span>
            </div>
            <p className="text-2xl font-bold text-white">{companyData.rating || "N/A"}</p>
            <p className="text-xs text-slate-500">{scoreBreakdown.rating?.score || 0}/25 pts</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-slate-400">Reviews</span>
            </div>
            <p className="text-2xl font-bold text-white">{companyData.reviews_count || 0}</p>
            <p className="text-xs text-slate-500">{scoreBreakdown.reviews?.score || 0}/25 pts</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-400">Completeness</span>
            </div>
            <p className="text-2xl font-bold text-white">{scoreBreakdown.completeness?.score || 0}/25</p>
            <p className="text-xs text-slate-500">Profile info</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-slate-400">Photos</span>
            </div>
            <p className="text-2xl font-bold text-white">{gbpData.photo_count || 0}</p>
            <p className="text-xs text-slate-500">{scoreBreakdown.photos?.score || 0}/25 pts</p>
          </div>
        </div>
      </div>

      {companyData.name && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-400" /> Current Profile Status
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Clock, label: "Hours", value: gbpData.hours, present: "Listed" },
              { icon: Phone, label: "Phone", value: gbpData.phone, present: "Listed" },
              { icon: Globe, label: "Website", value: gbpData.website, present: "Linked" },
              { icon: Camera, label: "Photos", value: gbpData.has_photos, present: `${gbpData.photo_count || 0} uploaded` },
            ].map(({ icon: Icon, label, value, present }) => (
              <div key={`gbp-${label}`} className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${value ? "text-emerald-400" : "text-slate-600"}`} />
                <div>
                  <p className="text-sm text-slate-400">{label}</p>
                  <p className={`font-medium ${value ? "text-emerald-400" : "text-red-400"}`}>
                    {value ? present : "Missing"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {gbpRecs.priority_actions && gbpRecs.priority_actions.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" /> Priority Actions
          </h3>
          <div className="space-y-4">
            {gbpRecs.priority_actions.map((action, i) => (
              <div key={`action-${action.action}-${i}`} className={`p-4 rounded-xl border ${getPriorityColor(action.priority)}`}>
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-white">{action.action}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(action.priority)}`}>{action.priority}</span>
                </div>
                <p className="text-slate-300 text-sm mb-3">{action.description}</p>
                {action.tips && (
                  <ul className="space-y-1">
                    {action.tips.map((tip) => (
                      <li key={`tip-${tip.slice(0, 30)}`} className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" /> {tip}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {gbpRecs.optimization_tips && gbpRecs.optimization_tips.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" /> Ongoing Optimization Tips
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {gbpRecs.optimization_tips.map((tip, i) => (
              <div key={`opt-${tip.tip}-${i}`} className="p-4 bg-slate-800/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-emerald-400">{tip.tip}</h4>
                  <span className="text-xs text-slate-500 bg-slate-700 px-2 py-1 rounded">{tip.frequency}</span>
                </div>
                <p className="text-slate-300 text-sm">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {gbpRecs.competitor_insights && gbpRecs.competitor_insights.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" /> Competitor GBP Comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Business</th>
                  <th className="text-center py-3 px-4 text-slate-400 font-medium">Rating</th>
                  <th className="text-center py-3 px-4 text-slate-400 font-medium">Reviews</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Gap</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-800 bg-emerald-500/10">
                  <td className="py-3 px-4 font-medium text-emerald-400">
                    {companyData.name || brandInput.company_name} (You)
                  </td>
                  <td className="text-center py-3 px-4 text-white">{companyData.rating || "N/A"}</td>
                  <td className="text-center py-3 px-4 text-white">{companyData.reviews_count || 0}</td>
                  <td className="py-3 px-4 text-slate-400">-</td>
                </tr>
                {gbpRecs.competitor_insights.map((comp) => (
                  <tr key={`gbpcomp-${comp.competitor}`} className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white">{comp.competitor}</td>
                    <td className="text-center py-3 px-4 text-white">{comp.rating || "N/A"}</td>
                    <td className="text-center py-3 px-4 text-white">{comp.reviews || 0}</td>
                    <td className="py-3 px-4">
                      {comp.rating_gap && <span className="text-red-400 text-sm">-{comp.rating_gap} rating</span>}
                      {comp.reviews_gap && <span className="text-orange-400 text-sm ml-2">-{comp.reviews_gap} reviews</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(gbpRecs.competitive_gap || gbpRecs.review_gap) && (
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
              <h4 className="font-semibold text-yellow-400 mb-2">Action Needed</h4>
              {gbpRecs.competitive_gap && <p className="text-slate-300 text-sm mb-1">{gbpRecs.competitive_gap.action}</p>}
              {gbpRecs.review_gap && <p className="text-slate-300 text-sm">{gbpRecs.review_gap.action}</p>}
            </div>
          )}
        </div>
      )}

      {gbpRecs.profile_status === "not_found" && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-8 text-center">
          <Building2 className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Google Business Profile Not Found</h3>
          <p className="text-slate-400 mb-4">
            We couldn&apos;t find a Google Business Profile for your business. This is a critical issue for local visibility.
          </p>
          <a href="https://business.google.com" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors">
            <Building2 className="w-5 h-5" /> Create Your Profile Now
          </a>
        </div>
      )}
    </div>
  );
};
